import { writeStorage } from "./browserStorage";

const listeners = new Set();
const observed = new WeakSet();
let pendingRegistration = null;

export const subscribeToUpdates = (listener) => {
  listeners.add(listener);
  if (pendingRegistration?.waiting) listener(pendingRegistration);
  return () => listeners.delete(listener);
};

export const observeRegistration = (registration) => {
  if (!registration) return;
  const announce = () => {
    if (registration.waiting && (registration.active || navigator.serviceWorker?.controller)) {
      pendingRegistration = registration;
      listeners.forEach((listener) => listener(registration));
    }
  };
  announce();
  if (observed.has(registration)) return;
  observed.add(registration);
  const observeInstalling = () => {
    const worker = registration.installing;
    if (!worker) return;
    const changed = () => {
      if (worker.state === "installed" || worker.state === "redundant") {
        worker.removeEventListener("statechange", changed);
        announce();
      }
    };
    worker.addEventListener("statechange", changed);
    changed();
  };
  registration.addEventListener("updatefound", observeInstalling);
  observeInstalling();
};

const waitForInstallation = (worker) => new Promise((resolve, reject) => {
  const finish = (error) => {
    clearTimeout(timer);
    worker.removeEventListener("statechange", changed);
    error ? reject(error) : resolve();
  };
  const changed = () => {
    if (["installed", "activating", "activated"].includes(worker.state)) finish();
    else if (worker.state === "redundant") finish(new Error("Update installation failed"));
  };
  const timer = setTimeout(() => finish(new Error("Update installation timed out")), 20000);
  worker.addEventListener("statechange", changed);
  changed();
});

const activate = (worker) => new Promise((resolve, reject) => {
  const serviceWorker = navigator.serviceWorker;
  const finish = (error) => {
    clearTimeout(timer);
    serviceWorker.removeEventListener("controllerchange", changed);
    worker.removeEventListener("statechange", changed);
    error ? reject(error) : resolve();
  };
  const changed = () => {
    if (serviceWorker.controller === worker) finish();
    else if (worker.state === "redundant") finish(new Error("Update activation failed"));
  };
  const timer = setTimeout(() => finish(new Error("Update activation timed out")), 20000);
  serviceWorker.addEventListener("controllerchange", changed);
  worker.addEventListener("statechange", changed);
  try {
    worker.postMessage({ type: "SKIP_WAITING" });
    changed();
  } catch (error) { finish(error); }
});

export const applyUpdate = async ({ registration, version, reload = () => window.location.reload() } = {}) => {
  if (navigator.onLine === false) throw new Error("Offline");
  const current = registration || await navigator.serviceWorker?.getRegistration(`${process.env.PUBLIC_URL || ""}/`);
  if (current) {
    if (!current.waiting) {
      await current.update();
      if (current.installing) await waitForInstallation(current.installing);
    }
    if (current.waiting) await activate(current.waiting);
  }
  // Leave the cart, language, and other applications' caches intact.
  // Only acknowledge the version after installation/activation succeeds.
  if (version) writeStorage("version", version);
  reload();
};
